import type { Citation } from "./types";

export interface TranscriptParagraphLike {
  start: number;
  end: number;
  sentences?: Array<{ text?: string }>;
  [key: string]: unknown;
}

export interface CitationTarget {
  index: number;
  seconds: number;
}

/** Resolve a citation to the containing transcript paragraph and video offset. */
export function resolveCitationTarget(
  citation: Pick<Citation, "startSeconds" | "citedText" | "locator">,
  paragraphs: TranscriptParagraphLike[] | null | undefined,
): CitationTarget {
  const requestedSeconds = Number.isFinite(citation.startSeconds)
    ? Math.max(0, citation.startSeconds)
    : 0;

  // Frame citations are grounded to an exact storyboard-derived offset. A
  // transcript paragraph may overlap that frame, but snapping to its start can
  // move the reviewer several seconds away from the cited visual evidence.
  if (citation.locator.kind === "frame") {
    const index =
      paragraphs?.findIndex(
        (paragraph) =>
          Number.isFinite(paragraph.start) &&
          Number.isFinite(paragraph.end) &&
          requestedSeconds >= paragraph.start &&
          requestedSeconds < paragraph.end,
      ) ?? -1;
    return { index, seconds: requestedSeconds };
  }

  if (!paragraphs?.length) {
    return { index: -1, seconds: requestedSeconds };
  }

  const validParagraphs = paragraphs
    .map((paragraph, index) => ({ paragraph, index }))
    .filter(
      ({ paragraph }) =>
        Number.isFinite(paragraph.start) && Number.isFinite(paragraph.end),
    );
  if (validParagraphs.length === 0) {
    return { index: -1, seconds: requestedSeconds };
  }

  const containing = validParagraphs.find(
    ({ paragraph }) =>
      requestedSeconds >= paragraph.start && requestedSeconds < paragraph.end,
  );
  if (containing) {
    return {
      index: containing.index,
      seconds: Math.max(0, containing.paragraph.start),
    };
  }

  const needle = citation.citedText.trim().toLocaleLowerCase().slice(0, 80);
  if (needle) {
    const textMatch = validParagraphs.find(({ paragraph }) =>
      (paragraph.sentences ?? []).some((sentence) =>
        (sentence.text ?? "").toLocaleLowerCase().includes(needle),
      ),
    );
    if (textMatch) {
      return {
        index: textMatch.index,
        seconds: Math.max(0, textMatch.paragraph.start),
      };
    }
  }

  const nearest = validParagraphs.reduce((best, candidate) =>
    Math.abs(candidate.paragraph.start - requestedSeconds) <
    Math.abs(best.paragraph.start - requestedSeconds)
      ? candidate
      : best,
  );
  return {
    index: nearest.index,
    seconds: Math.max(0, nearest.paragraph.start),
  };
}
